import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { ArrowDownTrayIcon, ArrowLeftIcon, ArrowUpTrayIcon, EyeIcon, PaperAirplaneIcon, PlusIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { privateListsApi } from "../../api/hliLists";
import { errorMessageOf, problemOf } from "../../api/scholarships";
import { Button, Card, ConfirmModal, Container, EmptyState, InfoBox, Input, Loading, Modal, PageHeader, RowAction, Select, Table } from "../ui";
import { parseExcelFile } from "../SpreadsheetUpload";
import { useDraftForm } from "../calls/forms";
import SaveChangesBar from "../calls/SaveChangesBar";
import { useYearOptions } from "../bursaries/shared";
import { useCurrentAcademicYear } from "../../hooks/useAcademicYears";
import StudentDialog, { studentErrors, toEntry } from "./StudentDialog";
import { ListHistory, ListStateTag, ListTotals, StudentsTable, listColumns } from "./ListParts";
import { HLI_LISTS_PATH, KIND_LABELS, KIND_OPTIONS, PRIVATE_LISTS_KEY, nameOf, studentsLabel, useList, useMyLists } from "./model";

// The sheet's columns, in the template's order, and the student field each fills
const SHEET = [
  ["Reg number", "regNumber"],
  ["National ID", "nationalId"],
  ["First name", "firstName"],
  ["Last name", "lastName"],
  ["Email", "email"],
  ["Programme", "programme"],
  ["Year of study", "yearOfStudy"],
  ["Tuition fee", "tuitionFee"],
  ["Allowance", "allowance"],
];

const downloadTemplate = () => {
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([SHEET.map(([column]) => column)]), "Students");
  XLSX.writeFile(workbook, "private-list-template.xlsx");
};

const keyOf = (regNumber) => String(regNumber).trim().toUpperCase();

// The private institution's loan officer's lists; a new one starts as a draft
export function HliListsPage() {
  const navigate = useNavigate();
  const years = useYearOptions();
  const { data = [], isPending, isError, error, refetch } = useMyLists();
  const [creating, setCreating] = useState(false);
  const open = (list) => navigate(`${HLI_LISTS_PATH}/${list.id}`);

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader
        title="Loan lists"
        subtitle="Your students for government loans or bursaries. Submit a list to HEC; the HEC Admin, the DG and the Minister decide on it, and approved lists go to BRD."
        actions={
          <Button onClick={() => setCreating(true)} icon={<PlusIcon className="h-4 w-4" />}>
            New list
          </Button>
        }
      />
      {isError ? (
        <EmptyState title="Could not load your lists" description={errorMessageOf(error, "Please try again.")} onRefresh={refetch} />
      ) : (
        <Table cardView viewKey="hli-lists"
          columns={[...listColumns(years, false), { key: "open", label: "", exportable: false, render: (l) => <RowAction Icon={EyeIcon} label="Open" onClick={() => open(l)} /> }]}
          data={data}
          rowKey={(l) => l.id}
          onRowClick={open}
          isLoading={isPending}
          defaultSort={{ key: "updated", direction: "desc" }}
          searchPlaceholder="Search by list"
          exportName="loan-lists"
          emptyTitle="No lists yet"
        />
      )}
      {creating && <NewListDialog years={years} onClose={() => setCreating(false)} onCreated={open} />}
    </Container>
  );
}

const EMPTY_LIST = { academicYearId: "", kind: "LOAN", title: "" };

function NewListDialog({ years, onClose, onCreated }) {
  const queryClient = useQueryClient();
  const { data: current } = useCurrentAcademicYear();
  const { form, errors, setErrors, update, reset } = useDraftForm("private-list-new", EMPTY_LIST);
  const [error, setError] = useState(null);
  const academicYearId = form.academicYearId || current?.id || years[0]?.value || "";

  const create = useMutation({
    mutationFn: (body) => privateListsApi.create(body),
    onSuccess: ({ data }) => {
      toast.success(`List "${data.title}" started`);
      queryClient.invalidateQueries({ queryKey: [PRIVATE_LISTS_KEY] });
      reset();
      onCreated(data);
    },
    onError: (err) => {
      console.error("Failed to start a list:", err);
      setError(errorMessageOf(err, "Failed to start the list. Please try again."));
    },
  });

  const submit = (e) => {
    e.preventDefault();
    setError(null);
    if (!form.title.trim()) return setErrors({ title: "Name the list." });
    create.mutate({ academicYearId, kind: form.kind, title: form.title.trim() });
  };

  const cancel = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissible={!create.isPending}
      title="New list"
      description="Start a draft, add the students, then submit it to HEC."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={cancel} disabled={create.isPending}>
            Cancel
          </Button>
          <Button size="sm" type="submit" form="private-list-new" loading={create.isPending}>
            Start list
          </Button>
        </>
      }
    >
      <form id="private-list-new" onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Input name="title" label="Name" required maxLength={200} placeholder="e.g. Year 1 loans 2026/27" value={form.title} onChange={update} error={errors.title} />
        <Select name="academicYearId" label="Academic year" required options={years} value={academicYearId} onChange={update} />
        <Select name="kind" label="For" required options={KIND_OPTIONS} value={form.kind} onChange={update} />
        {error && <p className="text-sm text-red-600">{error}</p>}
      </form>
    </Modal>
  );
}

// The list's details and students as they are sent back to the API
const fromView = (list) => ({
  academicYearId: list.academicYearId,
  kind: list.kind,
  title: list.title,
  entries: list.entries.map(({ decision, decisionReason, ...entry }) => entry),
});

// The server's refusals of single students, as "Row n (name): field"
const entryProblems = (problem, entries) =>
  (problem?.errors ?? [])
    .map((v) => {
      const [, index, field] = v.field?.match(/^entries\[(\d+)\]\.?(\w*)/) ?? [];
      if (index == null) return null;
      const entry = entries[Number(index)];
      return `${entry ? nameOf(entry) || entry.regNumber : `Student ${Number(index) + 1}`}: ${field || "this student"} is not valid`;
    })
    .filter(Boolean);

// One of the officer's lists: a draft is edited here and submitted; after that it can only be followed
export function HliListPage() {
  const { listId } = useParams();
  const { data: list, isPending, isError, refetch } = useList(listId, true);

  if (isPending) return <Loading label="Loading the list…" />;
  if (isError || !list) return <EmptyState title="This list was not found" onRefresh={refetch} />;
  return list.state === "DRAFT" ? <DraftList key={list.version} list={list} /> : <SentList list={list} />;
}

function BackLink() {
  return (
    <Link to={HLI_LISTS_PATH} className="flex w-fit items-center gap-1 text-sm font-medium text-brand hover:underline">
      <ArrowLeftIcon className="h-4 w-4" /> All lists
    </Link>
  );
}

function DraftList({ list }) {
  const queryClient = useQueryClient();
  const years = useYearOptions();
  const saved = fromView(list);
  const [draft, setDraft] = useState(saved);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [problems, setProblems] = useState([]);
  const fileInput = useRef(null);

  const changed = JSON.stringify(draft) !== JSON.stringify(saved);
  const totals = { students: draft.entries.length, totalTuition: draft.entries.reduce((n, e) => n + e.tuitionFee, 0), totalAllowance: draft.entries.reduce((n, e) => n + e.allowance, 0) };
  const refresh = () => queryClient.invalidateQueries({ queryKey: [PRIVATE_LISTS_KEY] });

  const save = useMutation({
    mutationFn: () => privateListsApi.change(list.id, { ...draft, title: draft.title.trim() }),
    onSuccess: () => {
      toast.success("List saved");
      setProblems([]);
      refresh();
    },
    onError: (err) => {
      console.error(`Failed to save list ${list.id}:`, err);
      setProblems(entryProblems(problemOf(err), draft.entries));
      toast.error(errorMessageOf(err, "Failed to save the list. Please try again."));
    },
  });

  const submit = useMutation({
    mutationFn: () => privateListsApi.submit(list.id),
    onSuccess: () => {
      toast.success("List submitted to HEC");
      setSubmitting(false);
      refresh();
    },
    onError: (err) => {
      console.error(`Failed to submit list ${list.id}:`, err);
      setSubmitting(false);
      toast.error(errorMessageOf(err, "Failed to submit the list. Please try again."));
    },
  });

  const set = (e) => setDraft((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const takenBy = (except) => new Set(draft.entries.filter((e) => e !== except).map((e) => keyOf(e.regNumber)));

  const saveStudent = (entry) => {
    setDraft((prev) => ({ ...prev, entries: editing.entry ? prev.entries.map((e) => (e === editing.entry ? entry : e)) : [...prev.entries, entry] }));
    setEditing(null);
  };

  // Students from a sheet join the list; a registration number already on it replaces that student
  const importSheet = async (file) => {
    if (!file) return;
    setProblems([]);
    try {
      const rows = await parseExcelFile(file);
      if (!rows.length) throw new Error("The sheet has no rows under its header.");
      const students = rows.map((row) => Object.fromEntries(SHEET.map(([column, field]) => [field, row[column] ?? ""])));
      const seen = new Set();
      const found = students.flatMap((s, i) => {
        const errors = studentErrors(s, seen);
        seen.add(keyOf(s.regNumber));
        return Object.values(errors).map((message) => `Row ${i + 2}: ${message}`);
      });
      if (found.length) {
        setProblems(found);
        toast.error(`${found.length} problem${found.length === 1 ? "" : "s"} in the sheet. Nothing was added.`);
        return;
      }
      const entries = students.map(toEntry);
      const incoming = new Set(entries.map((e) => keyOf(e.regNumber)));
      setDraft((prev) => ({ ...prev, entries: [...prev.entries.filter((e) => !incoming.has(keyOf(e.regNumber))), ...entries] }));
      toast.success(`${studentsLabel(entries.length)} added from ${file.name}. Save to keep them.`);
    } catch (err) {
      console.error(`Failed to read the sheet ${file.name}:`, err);
      toast.error(err.message);
    } finally {
      fileInput.current.value = "";
    }
  };

  return (
    <Container className="flex flex-col gap-6 py-6">
      <BackLink />
      <PageHeader
        title={list.title}
        subtitle={`Draft · ${KIND_LABELS[draft.kind]} · ${list.academicYearName ?? ""}`}
        actions={
          <Button onClick={() => setSubmitting(true)} disabled={changed || !list.entries.length} icon={<PaperAirplaneIcon className="h-4 w-4" />}>
            Submit to HEC
          </Button>
        }
      />
      {changed && <InfoBox>Save your changes before submitting.</InfoBox>}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (draft.title.trim()) save.mutate();
        }}
        className="flex flex-col gap-6"
      >
        <Card title="Details" bodyClassName="grid gap-4 sm:grid-cols-3">
          <Input name="title" label="Name" required maxLength={200} value={draft.title} onChange={set} error={draft.title.trim() ? undefined : "Name the list."} />
          <Select name="academicYearId" label="Academic year" required options={years} value={draft.academicYearId} onChange={set} />
          <Select name="kind" label="For" required options={KIND_OPTIONS} value={draft.kind} onChange={set} />
        </Card>

        <ListTotals {...totals} />

        <Card
          title="Students"
          actions={
            <div className="flex flex-wrap gap-2">
              <Button variant="ghost" size="sm" onClick={downloadTemplate} icon={<ArrowDownTrayIcon className="h-4 w-4" />}>
                Template
              </Button>
              <Button variant="outline" size="sm" onClick={() => fileInput.current.click()} icon={<ArrowUpTrayIcon className="h-4 w-4" />}>
                Import sheet
              </Button>
              <Button size="sm" onClick={() => setEditing({ entry: null })} icon={<UserPlusIcon className="h-4 w-4" />}>
                Add student
              </Button>
              <input ref={fileInput} type="file" accept=".xlsx,.xls" className="hidden" aria-label="Students sheet" onChange={(e) => importSheet(e.target.files?.[0])} />
            </div>
          }
        >
          {problems.length > 0 && (
            <ul className="thin-scrollbar mb-4 flex max-h-48 flex-col gap-1 overflow-y-auto rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
          <StudentsTable
            entries={draft.entries}
            exportName={`${list.title}-students`}
            onEdit={(entry) => setEditing({ entry })}
            onRemove={setRemoving}
            emptyTitle="Add students one by one or import a sheet"
          />
        </Card>

        <SaveChangesBar count={changed ? 1 : 0} saving={save.isPending} onDiscard={() => setDraft(saved)} />
      </form>

      {editing && <StudentDialog student={editing.entry} taken={takenBy(editing.entry)} onSave={saveStudent} onClose={() => setEditing(null)} />}
      <ConfirmModal
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={() => {
          setDraft((prev) => ({ ...prev, entries: prev.entries.filter((e) => e !== removing) }));
          setRemoving(null);
        }}
        title="Remove this student?"
        description={removing ? `${nameOf(removing)} (${removing.regNumber}) leaves the list when you save.` : null}
        confirmLabel="Remove"
        destructive
      />
      <ConfirmModal
        open={submitting}
        onClose={() => setSubmitting(false)}
        onConfirm={() => submit.mutate()}
        loading={submit.isPending}
        title="Submit this list to HEC?"
        description={`${studentsLabel(list.entries.length)}. The list can no longer change once submitted.`}
        confirmLabel="Submit"
      />
    </Container>
  );
}

// A submitted list, followed through HEC, the DG and the Minister
function SentList({ list }) {
  return (
    <Container className="flex flex-col gap-6 py-6">
      <BackLink />
      <PageHeader title={list.title} subtitle={`${KIND_LABELS[list.kind]} · ${list.academicYearName ?? ""}`} actions={<ListStateTag state={list.state} />} />
      {list.state === "REJECTED" && list.reason && <InfoBox>Rejected: {list.reason}</InfoBox>}
      <ListTotals students={list.students} totalTuition={list.totalTuition} totalAllowance={list.totalAllowance} />
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <StudentsTable entries={list.entries} exportName={`${list.title}-students`} />
        <ListHistory history={list.history} />
      </div>
    </Container>
  );
}
